import { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  Grid, InputAdornment, IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Defined at module scope (NOT inside ChangePassword) so its identity is stable
// across renders. If it lived inside the component, every keystroke would remount
// the input and drop focus — which on mobile makes the keyboard disappear.
const PwdField = ({ label, value, onChange, show, onToggle }) => (
  <TextField
    label={label} type={show ? 'text' : 'password'} value={value} onChange={onChange}
    fullWidth size="small"
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton size="small" onClick={onToggle} edge="end">
            {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
);

const ChangePassword = () => {
  const [current,  setCurrent]  = useState('');
  const [next,     setNext]     = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showC,    setShowC]    = useState(false);
  const [showN,    setShowN]    = useState(false);
  const [error,    setError]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const navigate = useNavigate();

  const handleSave = async () => {
    if (!current || !next || !confirm) { setError('All fields are required'); return; }
    if (next.length < 6) { setError('New password must be at least 6 characters'); return; }
    if (next !== confirm) { setError('Passwords do not match'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/api/users/change-password`, { currentPassword: current, newPassword: next }, { headers: hdrs() });
      // Update stored user flag
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...user, mustChangePassword: false }));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally { setSaving(false); }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f6f8' }}>
      <Box sx={{ width: '100%', maxWidth: 400, px: 2 }}>
        <Card elevation={4} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <Lock color="warning" />
              <Typography variant="h6" fontWeight={700}>Change Your Password</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Your administrator has required you to set a new password before continuing.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PwdField label="Current Password" value={current} onChange={e => setCurrent(e.target.value)}
                  show={showC} onToggle={() => setShowC(v => !v)} />
              </Grid>
              <Grid item xs={12}>
                <PwdField label="New Password" value={next} onChange={e => setNext(e.target.value)}
                  show={showN} onToggle={() => setShowN(v => !v)} />
              </Grid>
              <Grid item xs={12}>
                <PwdField label="Confirm New Password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  show={showN} onToggle={() => setShowN(v => !v)} />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" fullWidth size="large" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Set New Password'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ChangePassword;
